import { useCallback, useEffect, useRef, useState } from 'react';
import './ProcessRoadmap.css';

function getColumnCount(width) {
  if (width <= 560) return 1;
  if (width <= 1024) return 2;
  return 4;
}

/**
 * Orthogonal path with rounded 90° corners (travel / itinerary snake).
 * points: [{x,y}, ...]
 */
function buildRoundedOrthoPath(points, radius = 22) {
  if (points.length < 2) return '';

  let d = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    if (!next) {
      d += ` L ${curr.x} ${curr.y}`;
      break;
    }

    const inDx = curr.x - prev.x;
    const inDy = curr.y - prev.y;
    const outDx = next.x - curr.x;
    const outDy = next.y - curr.y;

    const inLen = Math.hypot(inDx, inDy) || 1;
    const outLen = Math.hypot(outDx, outDy) || 1;
    const r = Math.min(radius, inLen / 2.2, outLen / 2.2);

    const x1 = curr.x - (inDx / inLen) * r;
    const y1 = curr.y - (inDy / inLen) * r;
    const x2 = curr.x + (outDx / outLen) * r;
    const y2 = curr.y + (outDy / outLen) * r;

    // Sweep: cross product of incoming × outgoing
    const cross = inDx * outDy - inDy * outDx;
    const sweep = cross < 0 ? 0 : 1;

    d += ` L ${x1} ${y1}`;
    d += ` A ${r} ${r} 0 0 ${sweep} ${x2} ${y2}`;
  }

  return d;
}

/**
 * Serpentine itinerary:
 * 01──02──03──04
 *                │
 * ╭──────────────╯
 * │
 * ╰──05──06──07──08
 */
function buildSerpentineRoute(cards, cols, wrapW) {
  if (cards.length < 2) return { points: [], turns: [] };

  const points = [];
  const turns = [];
  const edgePad = 16;

  // ——— Single column: soft vertical channel on the left ———
  if (cols === 1) {
    cards.forEach((c, i) => {
      points.push({ x: Math.max(10, c.left - 20), y: c.cy });
      if (i < cards.length - 1) {
        turns.push({
          x: Math.max(10, c.left - 20),
          y: (c.cy + cards[i + 1].cy) / 2,
          key: `v-${i}`,
        });
      }
    });
    // Only keep a couple of pins
    const pinTurns = turns.filter((_, i) => i === 0 || i === turns.length - 1);
    return { points, turns: pinTurns.slice(0, 2) };
  }

  const rowCount = Math.ceil(cards.length / cols);

  for (let row = 0; row < rowCount; row += 1) {
    const rowCards = cards.slice(row * cols, row * cols + cols);
    if (!rowCards.length) continue;

    const rowY = rowCards.reduce((s, c) => s + c.cy, 0) / rowCards.length;
    const rowBottom = Math.max(...rowCards.map((c) => c.bottom));
    const first = rowCards[0];
    const last = rowCards[rowCards.length - 1];

    // Horizontal spine through this row (visible in gaps between cards)
    const startX = first.left + 8;
    const endX = last.right - 8;

    if (row === 0) {
      points.push({ x: startX, y: rowY });
    } else {
      // Arrive from the left snake into the start of this row
      points.push({ x: startX, y: rowY });
    }

    // Midpoints in the gutters between adjacent cards on this row
    for (let c = 0; c < rowCards.length - 1; c += 1) {
      const a = rowCards[c];
      const b = rowCards[c + 1];
      points.push({ x: (a.right + b.left) / 2, y: rowY });
    }
    points.push({ x: endX, y: rowY });

    // Snake to next row (right → down → left → down into next row left)
    if (row < rowCount - 1) {
      const nextCards = cards.slice((row + 1) * cols, (row + 2) * cols);
      const nextY = nextCards.reduce((s, c) => s + c.cy, 0) / nextCards.length;
      const nextFirst = nextCards[0];
      const gapTop = rowBottom;
      const gapBottom = Math.min(...nextCards.map((c) => c.top));
      const midY = gapTop + (gapBottom - gapTop) * 0.5;

      const rightX = Math.min(wrapW - edgePad, last.right + 22);
      const leftX = Math.max(edgePad, nextFirst.left - 8);

      // Pins on the two main corners of the snake
      const t2 = { x: rightX, y: midY, key: `turn-${row}-br` };
      const t3 = { x: leftX, y: midY, key: `turn-${row}-bl` };

      // Avoid duplicating the row end point if equal
      if (Math.abs(endX - rightX) > 1) {
        points.push({ x: rightX, y: rowY });
      }
      points.push({ x: rightX, y: midY });
      points.push({ x: leftX, y: midY });
      points.push({ x: leftX, y: nextY });

      turns.push(t2, t3);
    }
  }

  return { points, turns };
}

function MapPin({ x, y }) {
  return (
    <g className="process-roadmap-pin" transform={`translate(${x}, ${y})`}>
      <circle className="process-roadmap-pin-ring" r="6" />
      <circle className="process-roadmap-pin-dot" r="2.5" />
    </g>
  );
}

/**
 * Serpentine travel-route roadmap:
 * 01──02──03──04
 *                │
 * ╭──────────────╯
 * │
 * ╰──05──06──07──08
 */
export default function ProcessRoadmap({ steps }) {
  const wrapRef = useRef(null);
  const cardRefs = useRef([]);
  const pathRef = useRef(null);
  const [route, setRoute] = useState({ d: '', nodes: [], w: 0, h: 0 });
  const drawnRef = useRef(false);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const wrapRect = wrap.getBoundingClientRect();
    const cols = getColumnCount(window.innerWidth);

    const cards = cardRefs.current
      .slice(0, steps.length)
      .map((el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const left = r.left - wrapRect.left;
        const top = r.top - wrapRect.top;
        return {
          left,
          top,
          right: left + r.width,
          bottom: top + r.height,
          cx: left + r.width / 2,
          cy: top + r.height / 2,
          width: r.width,
          height: r.height,
        };
      })
      .filter(Boolean);

    if (cards.length < 2) return;

    const { points, turns } = buildSerpentineRoute(cards, cols, wrap.offsetWidth);

    setRoute({
      d: buildRoundedOrthoPath(points, 26),
      nodes: turns,
      w: wrap.offsetWidth,
      h: wrap.offsetHeight,
    });
  }, [steps.length]);

  useEffect(() => {
    const run = () => {
      requestAnimationFrame(() => {
        measure();
        requestAnimationFrame(measure);
      });
    };
    run();

    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const ro = new ResizeObserver(() => {
      drawnRef.current = false;
      measure();
    });
    ro.observe(wrap);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    const path = pathRef.current;
    const wrap = wrapRef.current;
    if (!path || !wrap || !route.d) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Let CSS own the running dash animation
    path.style.strokeDasharray = '';
    path.style.strokeDashoffset = '';

    if (reduce) {
      path.style.opacity = '1';
      return undefined;
    }

    if (!drawnRef.current) {
      path.style.opacity = '0.35';
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        drawnRef.current = true;
        path.style.transition = 'opacity 0.7s ease';
        path.style.opacity = '1';
        io.disconnect();
      },
      { threshold: 0.2 }
    );
    io.observe(wrap);

    return () => io.disconnect();
  }, [route.d]);

  return (
    <div className="process-roadmap" ref={wrapRef}>
      <svg
        className="process-roadmap-svg"
        width={route.w || '100%'}
        height={route.h || '100%'}
        viewBox={route.w && route.h ? `0 0 ${route.w} ${route.h}` : '0 0 1 1'}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <path ref={pathRef} className="process-roadmap-path" d={route.d} fill="none" />
        {route.nodes.map((n) => (
          <MapPin key={n.key} x={n.x} y={n.y} />
        ))}
      </svg>

      <ol className="process-roadmap-grid">
        {steps.map((step, index) => (
          <li
            key={step.n}
            className="process-roadmap-card"
            style={{ '--step-i': index }}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
          >
            <div className="process-roadmap-card-inner">
              <span className="process-roadmap-num-wrap" aria-hidden="true">
                <span className="process-roadmap-glow" />
                <span className="process-roadmap-num">{step.n}</span>
              </span>
              <span className="process-roadmap-title">{step.title}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
